import { Formik } from "formik";
import YUP from "custom_exports/yup_items";
import { Box, TextField } from "custom_exports/material_ui_items";
import { useLocation } from "react-router-dom";

// Spam content detection patterns
const SPAM_PATTERNS = {
	urls: /(?:https?:\/\/|www\.)/i,
	domains: /\.(com|net|org|io|co|xyz|tk|ml|ga|cf|gq|info|biz|ru|cn)\b/i,
	html: /<[^>]*>/,
	javascript: /(?:javascript:|onclick|onerror|onload|eval\(|alert\()/i,
};

// Helper function to check for spam content
const containsSpamContent = (value) => {
	if (!value) return false;
	return (
		SPAM_PATTERNS.urls.test(value) ||
		SPAM_PATTERNS.domains.test(value) ||
		SPAM_PATTERNS.html.test(value) ||
		SPAM_PATTERNS.javascript.test(value)
	);
};

// Helper function to count digits in a string
const countDigits = (str) => (str.match(/\d/g) || []).length;

function SignUpForm(props) {
	//--------------------------------- variables ---------------------------------//
	// ---- initialization ---- //
	// ---- destructuring ---- //
	const { handleForm, formRef } = props;

	// ---- assignments ---- //
	const location = useLocation();
	const mobileCountryCode = "+1";
	const formInitialValues = {
		email: location.state?.email || "",
		businessEmail: "",
		businessName: "",
		businessPhone: "",
		businessAddress: "",
	};

	const formValidationSchema = YUP.object().shape({
		email: YUP.string()
			.email("Provide a valid email")
			.max(254, "Email must be within 254 characters")
			.required("Email required"),
		businessEmail: YUP.string()
			.email("Provide a valid email")
			.max(254, "Email must be within 254 characters")
			.required("Business email required"),
		businessName: YUP.string()
			.required("Business name required")
			.min(2, "Business name must be at least 2 characters")
			.max(30, "Business name must be within 30 characters")
			.test(
				"no-spam-content",
				"Business name contains invalid content (URLs, HTML, or scripts not allowed)",
				(value) => !containsSpamContent(value)
			),
		businessPhone: YUP.string()
			.required("Phone number required")
			.max(20, "Phone number must be within 20 characters")
			.matches(
				/^[\d\s\+\-\(\)]+$/,
				"Phone number can only contain digits, +, -, (, ), and spaces"
			)
			.test(
				"min-digits",
				"Phone number must contain at least 7 digits",
				(value) => value && countDigits(value) >= 7
			),
		businessAddress: YUP.string()
			.required("Address required")
			.min(5, "Address must be at least 5 characters")
			.max(40, "Address must be within 40 characters")
			.test(
				"no-spam-content",
				"Address contains invalid content (URLs, HTML, or scripts not allowed)",
				(value) => !containsSpamContent(value)
			),
	});

	//--------------------------------- functions ---------------------------------//
	// ---- useEffects ---- //

	//--------------------------------- elements ----------------------------------//
	const element = (
		<Formik
			initialValues={formInitialValues}
			validationSchema={formValidationSchema}
			innerRef={formRef}
			onSubmit={(userInput) => {
				handleForm({
					type: "submit",
					data: {
						email: userInput.email,
						business_name: userInput.businessName,
						business_email: userInput.businessEmail,
						business_address: userInput.businessAddress,
						business_phone: userInput.businessPhone.startsWith("+")
							? userInput.businessPhone
							: mobileCountryCode + userInput.businessPhone,
					},
				});
			}}
		>
			{({
				values,
				errors,
				touched,
				// isSubmitting,
				handleBlur,
				handleChange,
				handleSubmit,
			}) => {
				return (
					<Box sx={{ mt: 1, width: "100%" }}>
						<TextField
							id="signup-form-login-email"
							type="email"
							name="email"
							label={errors.email || "Email"}
							autoComplete="email"
							// autoFocus
							required
							margin="normal"
							fullWidth
							value={values.email}
							error={Boolean(errors.email && touched.email)}
							onBlur={handleBlur}
							onChange={(event) => {
								handleChange(event);
								handleForm({
									type: "change",
									event,
								});
							}}
						/>
						<TextField
							id="signup-form-business-email"
							type="email"
							name="businessEmail"
							label={errors.businessEmail || "Business Email"}
							autoComplete="email"
							required
							margin="normal"
							fullWidth
							value={values.businessEmail}
							error={Boolean(
								errors.businessEmail && touched.businessEmail
							)}
							onBlur={handleBlur}
							onChange={(event) => {
								handleChange(event);
								handleForm({
									type: "change",
									event,
								});
							}}
						/>
						<TextField
							id="signup-form-business-name"
							type="text"
							name="businessName"
							label={errors.businessName || "Business Name"}
							required
							margin="normal"
							fullWidth
							value={values.businessName}
							error={Boolean(
								errors.businessName && touched.businessName
							)}
							onBlur={handleBlur}
							onChange={(event) => {
								handleChange(event);
								handleForm({
									type: "change",
									event,
								});
							}}
							inputProps={{ maxLength: 30 }}
							helperText={
								!errors.businessName || !touched.businessName
									? `${values.businessName.length}/30 characters`
									: ""
							}
						/>
						<TextField
							id="signup-form-business-phone"
							type="tel"
							name="businessPhone"
							label={errors.businessPhone || "Phone Number"}
							placeholder="(416) 555-1234"
							required
							margin="normal"
							fullWidth
							value={values.businessPhone}
							error={Boolean(
								errors.businessPhone && touched.businessPhone
							)}
							onBlur={handleBlur}
							onChange={(event) => {
								handleChange(event);
								handleForm({
									type: "change",
									event,
								});
							}}
							inputProps={{ maxLength: 20 }}
						/>
						<TextField
							id="signup-form-business-address"
							type="text"
							name="businessAddress"
							label={errors.businessAddress || "Address"}
							required
							margin="normal"
							fullWidth
							multiline
							rows={2}
							value={values.businessAddress}
							error={Boolean(
								errors.businessAddress && touched.businessAddress
							)}
							onBlur={handleBlur}
							onChange={(event) => {
								handleChange(event);
								handleForm({
									type: "change",
									event,
								});
							}}
							inputProps={{ maxLength: 40 }}
							helperText={
								!errors.businessAddress || !touched.businessAddress
									? `${values.businessAddress.length}/40 characters`
									: ""
							}
						/>
					</Box>
				);
			}}
		</Formik>
	);

	return element;
}

export default SignUpForm;
